<template>
    <tr :key="item.id"
        :class="[
            'tw-border-b tw-border-solid tw-border-gray-100 last:tw-border-b-0 tw-group even:tw-bg-gray-50/50 hover:tw-bg-primary-50/60 tw-duration-100',
            item.needs_attention && 'tw-bg-amber-50/80 tw-border-amber-200 hover:tw-bg-amber-100/80'
        ]">
        <td class="tw-px-5 tw-py-3 tw-text-sm">{{ item.id }}</td>
        <td class="tw-px-5 tw-py-3 tw-text-sm">
            <p class="tw-truncate">{{ item.customer_name }}</p>
        </td>
        <td class="tw-px-5 tw-py-3 tw-text-sm">
            <p class="tw-truncate tw-max-w-[100px]">{{ item.customer_phone }}</p>
        </td>

        <td class="tw-px-5 tw-py-3 tw-text-sm">
            <p class="tw-truncate tw-max-w-[70px]">{{ item.customer_city }}</p>
        </td>

        <td class="tw-px-5 tw-py-3 tw-text-sm">
            <div class="tw-flex tw-flex-col tw-gap-1 tw-text-sm">
                <div v-for="i in item.items" :key="i.id + i.updated_at" class="tw-flex tw-items-center tw-gap-2">
                    <span>{{ i.quantity }}</span>
                    <span class="tw-font-bold tw-text-primary-500">x</span>
                    <p class="tw-max-w-[120px] tw-truncate">{{ i.product_name }}</p>
                    <p v-if="i.variant_name" class="tw-max-w-[50px] tw-truncate tw-text-emerald-500 tw-font-bold">({{ i.variant_name }})</p>
                </div>

            </div>
        </td>

        <td class="tw-px-5 tw-py-3 tw-text-sm">
            <p class="tw-truncate tw-max-w-[70px] tw-font-bold tw-flex tw-items-center tw-gap-1">
                <span>{{ item.items.reduce((p, c) => p + parseFloat(c.price), 0).toFixed(2) }}</span>
                <span>{{ $t('currencies.' + CURRENCY, 'USD') }}</span>
            </p>
        </td>

        <td class="tw-px-5 tw-py-3 tw-text-sm">
            <p class="tw-truncate tw-max-w-[70px] tw-font-bold tw-flex tw-items-center tw-gap-1 tw-px-2 tw-text-cyan-500">
                {{ item.followup_calls }}
            </p>
        </td>

        <td class="tw-px-5 tw-py-3 tw-text-sm">
            <p class="tw-truncate tw-max-w-[100px] tw-text-xs tw-rounded tw-w-fit tw-px-4 tw-py-1 tw-mx-auto" :class="[reconfirmation.color]" v-if="reconfirmation?.name">
                {{ $t(reconfirmation.name) }}
            </p>
        </td>

        <td class="tw-px-5 tw-py-3 tw-text-sm">
            <div v-if="item.delivery_id" class="tw-flex tw-items-center tw-gap-2 tw-mx-auto tw-justify-center">
                
                <div class="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-w-fit  tw-p-1  tw-bg-gray-100 tw-px-4 tw-rounded-md tw-border tw-border-solid tw-border-gray-200 hover:tw-border-gray-400 tw-duration-200 tw-cursor-pointer">
                    <p  class="tw-truncate tw-max-w-[100px] tw-font-bold tw-text-xs tw-rounded tw-w-fit">
                        {{ delivery.name }}
                    </p>
                </div>
                <a v-if="item.external_tracking && false" target="_blank" :href="getOrderLink(item.external_tracking)">
                    <icon icon="mynaui:external-link" class="tw-text-base group-hover:tw-text-primary-500 tw-text-gray-400 tw-duration-200" />
                </a>
                <!-- Return Reason Button -->
                <div v-if="item.return_reason" class="tw-relative">
                    <button 
                        @click="handleReturnReasonClick"
                        :class="[!item.return_reason_read_at ? 'tw-border-red-300 tw-bg-red-100 tw-animate-pulse' : 'tw-bg-gray-100']"
                        class="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-full hover:tw-bg-gray-200 tw-duration-200 tw-border tw-border-solid tw-border-gray-200 hover:tw-border-gray-400"
                    >
                        <icon icon="duo-icons:alert-triangle" class="tw-text-base" :class="[!item.return_reason_read_at ? 'tw-text-red-700' : 'tw-text-gray-600']" />
                    </button>
                    <div 
                        v-if="showReturnReasonTooltip"
                        ref="tooltipRef"
                        class="tw-absolute tw-z-50 tw-bottom-full tw-left-1/2 tw-transform tw--translate-x-1/2 tw-mb-2 tw-px-3 tw-py-2 tw-bg-white tw-rounded-md tw-shadow-lg tw-border tw-border-solid tw-border-gray-200 tw-text-xs tw-whitespace-nowrap"
                    >
                        {{ item.return_reason }}
                    </div>
                </div>
            </div>
            <p v-else class="tw-truncate tw-max-w-[100px] tw-text-xs tw-rounded tw-w-fit tw-px-4 tw-py-1 tw-mx-auto">
                {{ $t('common.without') }}
            </p>
        </td>

        <td class="tw-px-5 tw-py-3 tw-text-sm">
            <p class="tw-truncate tw-max-w-[100px] tw-text-xs tw-rounded tw-w-fit tw-px-4 tw-py-1 tw-mx-auto" :class="[delivery_status.color]" v-if="delivery_status?.name">
                {{ $t(delivery_status.name) }}
            </p>
        </td>

        <td class="tw-px-5 tw-py-3 tw-text-sm">
            <p class="tw-text-sm tw-whitespace-nowrap tw-text-center" v-if="item.reconfirmed_at">{{ reconfirmedAt }}</p>
            <p class="tw-text-sm tw-whitespace-nowrap tw-text-center" v-else>-</p>
        </td>

        <td class="tw-px-5 tw-pr-0 tw-py-">
            <OrderTableActions :item="item" />
        </td>
    </tr>
</template>

<script setup>
import { defineProps, toRef, computed, ref } from "vue";
import OrderTableActions from "./OrderTableActions";
import { getReconfirmation, getDelivery } from "@/utils/status";
import { CURRENCY } from "@/config/app";
import { getOrderLink } from '@/utils/nawris'
import { useStateStore } from "@/stores/app/state";
import moment from "moment";
import { useI18n } from "vue-i18n";
import { returnReasonRead } from "@/api/orders";

const i18n = useI18n();
const props = defineProps(["item"]);
const item = toRef(props, "item");
const stateStore = useStateStore();

const reconfirmation = getReconfirmation(item.value.followup_status)
const delivery_status = getDelivery(item.value.delivery_status)

const reconfirmedAt = computed(() => {
    return moment(item.value.reconfirmed_at).locale(i18n.locale.value).fromNow()
})

const delivery = stateStore.alldeliveries.find(d => d.id == item.value.delivery_id)

// Return reason functionality
const showReturnReasonTooltip = ref(false);
const tooltipRef = ref(null);

const handleReturnReasonClick = async (event) => {
    event.stopPropagation();
    showReturnReasonTooltip.value = !showReturnReasonTooltip.value;

    if(!item.value.return_reason_read_at) {
        const response = await returnReasonRead(item.value.id);
        if (response.data.code === 'SUCCESS') {
            item.value.return_reason_read_at = response.data.order.return_reason_read_at;
        }
    }
};

</script>

<style></style>
